import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useToast } from '../../../../../../modules/components/toast/ToastContext';
import {
  CategorizeServiceSection,
  FieldStatus,
  KeywordType,
  SiService,
  keywordLabel,
  siAiCategoryReasonTranslationKey,
} from '../../../../mappings';
import KeywordTypeBadge from '../../../../../../modules/components/badge/KeywordTypeBadge';
import clsx from 'clsx';
import UrlLabelThumbnailCard from '../../card/UrlLabelThumbnailCard';
import SingleAccordion from '../../../../../../modules/components/accordion/SingleAccordion';
import { getActiveItemsOnly, getImagePathByCaptureId, getTranslatedValueWithFallback } from '../../utils';
import CategorizeKeywordTable from './CategorizeKeywordTable';
import { getCardId } from './CategoryDetail';
import { UsePageDataManagerResult } from '../../hooks/usePageDataManager';
import ToggleSwitch from '../../../../../../modules/components/toggleSwitch/ToggleSwitch';
import { SiAiLinksModal } from '../../modal/SiAiLinksModal';

interface Props {
  data: any;
  screenshots?: any[];
  status: string;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
  onExpandImages: (images: string[], title?: string) => void;
  isEditable?: boolean;
  isSiAiNewData?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
}

export default function CategorizeContent({
  data,
  screenshots,
  status,
  refetch,
  onExpandImages,
  translation,
  currentLanguage,
  isEditable,
  isSiAiNewData,
  onUpdate,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_CATEGORY_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const emDash = '—';
  const shouldRenderSiAiReadonlyStyle = !isEditable && !!isSiAiNewData;
  const [showCategoryLinksModal, setShowCategoryLinksModal] = useState(false);
  const [selectedCategoryLinks, setSelectedCategoryLinks] = useState<{
    category: string;
    urls: string[];
  }>(null);
  const [editingUrlKey, setEditingUrlKey] = useState<string | null>(null);
  const [editingUrlValue, setEditingUrlValue] = useState('');

  const categorySectionDetailIndex = data?.detail?.findIndex(
    (d) => d?.section === CategorizeServiceSection.CATEGORY
  );

  type CategorizeServiceDetail = {
    category: KeywordType;
    urls: Array<{
      url: string;
      images: string[];
      keywords?: Array<{
        keyword?: string;
        snippets?: string[];
        isHidden?: boolean;
      }>;
    }>;
    isHidden: boolean;
  };
  const categorizeServiceDetails: CategorizeServiceDetail[] = useMemo(() => {
    const res: CategorizeServiceDetail[] = [];

    const detail = data?.detail?.[categorySectionDetailIndex]?.section_detail ?? [];
    for (const item of detail) {
      const newCategoryObj: CategorizeServiceDetail = {
        category: item?.category,
        urls: [],
        isHidden:
          data?.result?.categories?.find((d) => d?.category === item?.category)?.field_status !==
          FieldStatus.ACTIVE,
      };

      for (const urlObj of item.url_detail) {
        const keywords = [];
        for (const keyword of urlObj?.keywords) {
          if (keyword?.field_status === FieldStatus.ACTIVE && newCategoryObj.isHidden) {
            newCategoryObj.isHidden = false;
          }
          keywords.push({
            keyword: keyword?.keyword,
            snippets: keyword?.snippets,
            isHidden: keyword?.field_status !== FieldStatus.ACTIVE,
          });
        }

        newCategoryObj.urls.push({
          url: urlObj?.url,
          images: urlObj?.capture_id
            ? [getImagePathByCaptureId(urlObj.capture_id, screenshots)]
            : [],
          keywords,
        });
      }
      res.push(newCategoryObj);
    }
    return res;
  }, [data, isEditable, screenshots]);

  const readonlySiAiCategories = useMemo(
    () =>
      (data?.result?.categories ?? []).filter((categoryObj) => {
        return categoryObj?.field_status === FieldStatus.ACTIVE;
      }),
    [data]
  );

  const getCategoryUrls = (category: string) => {
    const sectionDetails: any[] =
      data?.detail?.[categorySectionDetailIndex]?.section_detail ?? [];

    const urls: string[] = sectionDetails.reduce((acc: string[], item: any) => {
      if (item?.category === category) {
        (item?.url_detail ?? []).forEach((urlObj: any) => {
          if (typeof urlObj?.url === 'string') {
            acc.push(urlObj.url);
          }
        });
      }
      return acc;
    }, []);

    return Array.from(new Set<string>(urls));
  };

  if (shouldRenderSiAiReadonlyStyle) {
    return (
      <>
        {readonlySiAiCategories.map((categoryObj) => (
          (() => {
            const category = String(categoryObj?.category ?? '');
            const categoryUrls = getCategoryUrls(category);
            const hasLinks = categoryUrls.length > 0;

            return (
              <div
                key={categoryObj?.category}
                className='card border'
                id={getCardId(categoryObj?.category)}
                style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
              >
                <div
                  className='card-body p-6'
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedCategoryLinks({
                      category,
                      urls: categoryUrls,
                    });
                    setShowCategoryLinksModal(true);
                  }}
                >
                  <div className='d-flex align-items-center justify-content-between gap-6'>
                    <div className='d-flex align-items-center gap-6'>
                      <i className='bi bi-tag-fill text-warning fs-4'></i>
                      <div className='d-flex flex-column gap-2'>
                        <div className='fw-bolder'>
                          {intl.formatMessage({ id: `SI_KEYWORD_TYPE_BADGE.${categoryObj?.category as KeywordType}` })}
                        </div>
                        {categoryObj?.reason && <div>{getTranslatedValueWithFallback(
                          translation,
                          currentLanguage,
                          `sensitive_category.${siAiCategoryReasonTranslationKey[categoryObj?.category]}`,
                          categoryObj?.reason
                        )}</div>}
                        {/* {categoryObj?.link && (
                          <a href={categoryObj?.link} target='_blank' rel='noopener noreferrer'>
                            <i className='bi bi-box-arrow-up-right me-2'></i>
                            View Evidence Link
                          </a>
                        )} */}
                      </div>
                    </div>
                    {hasLinks && (
                      <i
                        className='bi bi-link-45deg fs-5 text-muted'
                        aria-hidden='true'
                      ></i>
                    )}
                  </div>
                </div>
              </div>
            );
          })()
        ))}

        <SiAiLinksModal
          show={showCategoryLinksModal}
          onClose={() => setShowCategoryLinksModal(false)}
          title={t('REFERENCE_URLS')}
          links={selectedCategoryLinks?.urls}
        />
      </>
    );
  }

  return (
    <>
      {categorizeServiceDetails.map((item, index) => {
        if (!isEditable && item.isHidden) {
          return null;
        }

        return (
          <div
            key={item.category}
            className={clsx('card border', item.isHidden && 'opacity-50')}
            id={getCardId(item.category)}
            style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
          >
            {isEditable && (
              <div className='card-header px-6'>
                <h4 className='card-title gap-4'>
                  <KeywordTypeBadge
                    type={item.category}
                    className={clsx('fs-base', item.isHidden && 'opacity-50')}
                  />
                  <ToggleSwitch
                    isVisible={!item.isHidden}
                    onToggle={() => {
                      onUpdate(SiService.CATEGORIZE_SERVICE, (data) => {
                        const newFieldStatus = item.isHidden
                          ? FieldStatus.ACTIVE
                          : FieldStatus.INACTIVE;
                        data.result.categories[index].field_status = newFieldStatus;
                        data.detail[categorySectionDetailIndex].section_detail[
                          index
                        ].url_detail?.forEach((u) => {
                          u.keywords?.forEach((k) => {
                            k.field_status = newFieldStatus;
                          });
                        });
                      });
                    }}
                  />
                </h4>
              </div>
            )}

            {isSiAiNewData && (
              <div className='bg-light py-4 px-6 d-flex flex-column gap-4'>
                <div className='d-flex flex-column flex-md-row gap-4 gap-md-9'>
                  <div className='d-flex flex-column flex-shrink-0'>
                    <span className='fw-bolder fs-7'>Detect By</span>
                    <span className='fs-7'>AI</span>
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='fw-bolder fs-7'>AI Reason</span>
                    <span className='fs-7'>{data?.result?.categories?.find((category) => category?.category === item.category)?.reason ?? emDash}</span>
                  </div>
                </div>
              </div>
            )}

            <div className='card-body p-6 vstack gap-4'>
              {/* URL Cards */}
              {item.urls?.map((urlObj, uIndex) => {
                const isAllHidden =
                  item.isHidden && !urlObj?.keywords?.some((keyword) => !keyword?.isHidden);
                const currentEditKey = `${index}-${uIndex}`;
                const isEditingUrl = isSiAiNewData && editingUrlKey === currentEditKey;
                const header = (
                  <UrlLabelThumbnailCard
                    key={uIndex}
                    url={urlObj?.url}
                    thumbnail={urlObj?.images?.[0]}
                    onClickThumbnail={() => {
                      onExpandImages(urlObj?.images);
                    }}
                    showThumbnaiPlaceholder
                    className={{
                      container: isEditable ? 'p-3' : 'border rounded p-3',
                    }}
                  />
                );
                const nonEditableHeader = (
                  <div
                    key={uIndex}
                    className='d-flex justify-content-between align-items-center gap-4 w-100 border rounded p-3'
                  >
                    <div className='flex-grow-1 min-w-0 pe-2'>
                      <UrlLabelThumbnailCard
                        url={urlObj?.url}
                        thumbnail={urlObj?.images?.[0]}
                        onClickThumbnail={() => {
                          onExpandImages(urlObj?.images);
                        }}
                        showThumbnaiPlaceholder
                      />
                    </div>
                    <div className='text-end flex-shrink-0'>
                      <KeywordTypeBadge type={item.category} className='fs-8' />
                    </div>
                  </div>
                );

                return isEditable ? (
                  <SingleAccordion
                    key={urlObj?.url}
                    header={
                      isEditingUrl ? (
                        <div
                          className='w-100 px-3 py-4'
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <input
                            className='form-control form-control-sm'
                            value={editingUrlValue}
                            onChange={(e) => setEditingUrlValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder='Enter URL'
                          />
                        </div>
                      ) : (
                        header
                      )
                    }
                    headerActions={
                      isSiAiNewData
                        ? isEditingUrl
                          ? (
                            <div className='d-flex align-items-center gap-2'>
                              <button
                                type='button'
                                className='btn btn-sm btn-light-success'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const trimmedUrl = editingUrlValue.trim();
                                  if (!trimmedUrl) {
                                    return;
                                  }

                                  onUpdate(SiService.CATEGORIZE_SERVICE, (data) => {
                                    const categoryDetail =
                                      data.detail[categorySectionDetailIndex].section_detail[index];
                                    categoryDetail.url_detail[uIndex].url = trimmedUrl;
                                  });

                                  setEditingUrlKey(null);
                                  setEditingUrlValue('');
                                }}
                                disabled={!editingUrlValue.trim()}
                              >
                                Save
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-light'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUrlKey(null);
                                  setEditingUrlValue('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className='d-flex align-items-center gap-2'>
                              <button
                                type='button'
                                className='btn btn-sm btn-light-primary'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUrlKey(currentEditKey);
                                  setEditingUrlValue(urlObj?.url ?? '');
                                }}
                              >
                                <i className='bi bi-pencil-square'></i>
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-light-danger'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onUpdate(SiService.CATEGORIZE_SERVICE, (data) => {
                                    const categoryDetail =
                                      data.detail[categorySectionDetailIndex].section_detail[index];
                                    categoryDetail.url_detail.splice(uIndex, 1);
                                  });

                                  if (editingUrlKey === `${index}-${uIndex}`) {
                                    setEditingUrlKey(null);
                                    setEditingUrlValue('');
                                  }
                                }}
                              >
                                <i className='bi bi-trash'></i>
                              </button>
                            </div>
                          )
                        : undefined
                    }
                    open={urlObj?.keywords?.length > 0}
                    className={{
                      body: 'border-top py-0 pb-3',
                    }}
                  >
                    <CategorizeKeywordTable
                      data={urlObj?.keywords}
                      onToggleVisibility={(keyword) => {
                        onUpdate(SiService.CATEGORIZE_SERVICE, (data) => {
                          const categoryDetail =
                            data.detail[categorySectionDetailIndex].section_detail[index];
                          const keywordObj = categoryDetail.url_detail[uIndex].keywords.find(
                            (k) => k.keyword === keyword
                          );
                          keywordObj.field_status =
                            keywordObj.field_status === FieldStatus.ACTIVE
                              ? FieldStatus.INACTIVE
                              : FieldStatus.ACTIVE;

                          const hasActiveKeywords = categoryDetail.url_detail.some((u) =>
                            u.keywords.some((k) => k.field_status === FieldStatus.ACTIVE)
                          );
                          data.result.categories.find(
                            (c) => c.category === item.category
                          ).field_status = hasActiveKeywords
                              ? FieldStatus.ACTIVE
                              : FieldStatus.INACTIVE;
                        });
                      }}
                    />
                  </SingleAccordion>
                ) : isAllHidden ? (
                  <></>
                ) : (
                  nonEditableHeader
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}
